import { getCurrentDate } from "@/utils/datetime";

import type { ScheduleEntry } from "./sentiment-check-slice";

/** A check window as ISO strings, `start` inclusive and `end` exclusive. */
export interface CheckWindow {
  start: string;
  end: string;
}

/**
 * Mirror of the server's `Cadmus.Sentiment.build_windows/2`: consecutive
 * schedules form windows and the last one closes at `dueDate`. No due date
 * means no windows.
 */
export function buildWindows(
  schedules: ScheduleEntry[],
  dueDate: string | null
): CheckWindow[] {
  if (dueDate === null) return [];
  return schedules.map((entry, i) => ({
    start: entry.scheduledAt,
    end: schedules[i + 1]?.scheduledAt ?? dueDate,
  }));
}

/**
 * The window the server would accept a submission in right now, if any.
 * Like the server, the latest matching window wins.
 */
export function findCurrentWindow(
  schedules: ScheduleEntry[],
  dueDate: string | null
): CheckWindow | null {
  const now = getCurrentDate();
  return (
    buildWindows(schedules, dueDate)
      .reverse()
      .find(
        (window) => new Date(window.start) <= now && now < new Date(window.end)
      ) ?? null
  );
}

/**
 * Find the next sentiment check date for the student.
 *
 * Returns the current window's start if the student has not checked in it
 * (triggers immediately), otherwise the start of the next non-empty window,
 * or `null` when no further window will open.
 *
 * Schedules [Feb 23, Mar 5, Mar 15], due Mar 25:
 *  - now=Mar 14, lastCheck=null  → "Mar 5"  (missed window, prompt now)
 *  - now=Mar 14, lastCheck=Mar 6 → "Mar 15" (current done, next window)
 *  - now=Feb 20, lastCheck=null  → "Feb 23" (wait for first schedule)
 *  - now=Mar 26                  → null     (past the due date)
 */
export function findNextSchedule(
  schedules: ScheduleEntry[],
  lastCheckDate: string | null,
  dueDate: string | null
): string | null {
  const now = getCurrentDate();
  const windows = buildWindows(schedules, dueDate);
  const first = windows[0];
  if (!first) return null;

  if (now < new Date(first.start)) return first.start;

  const current = findCurrentWindow(schedules, dueDate);
  if (!current) return null;

  const lastCheck = lastCheckDate !== null ? new Date(lastCheckDate) : null;
  if (!lastCheck || lastCheck < new Date(current.start)) return current.start;

  const next = windows.find(
    (window) =>
      window.start > current.start &&
      new Date(window.start) < new Date(window.end)
  );
  return next?.start ?? null;
}
